import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAcademicSessionsAsync } from "../../Redux/MasterSlice";
import BaseApi from "../../Http/BaseApi";
import Pagination from "../../Components/Utils/Pagination";

export default function Form16Upload() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [batchSize] = useState(20); // Upload 20 files at a time
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [batchResults, setBatchResults] = useState({
    totalUploaded: 0,
    totalErrors: 0,
    errors: [],
  });
  const [failedUploads, setFailedUploads] = useState([]);
  const [documents, setDocuments] = useState({
    data: [],
    links: [],
    total: 0,
    per_page: 20,
  });
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({
    pan: "",
    part: "",
    session: "",
  });
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const formatAcademicLabel = (label = "") =>
    label.slice(0, 5) + label.slice(7); // "2025-2026" → "2025-26"

  // Get academicSessions from Redux store
  const academicSessions = useSelector(
    (state) => state.master.academicSessions
  );

  useEffect(() => {
    // Fetch academic sessions from API
    dispatch(getAcademicSessionsAsync());
    // Load initial documents
    loadDocuments();
  }, [dispatch]);

  const loadDocuments = async (url = null) => {
    setLoading(true);
    try {
      const apiUrl = url && typeof url === "string" ? url : `/form16-documents`;
      const response = await BaseApi().get(apiUrl);
      setDocuments(response.data);
    } catch (error) {
      console.error("Error loading documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    // Validate file count
    if (files.length > 100) {
      alert("Maximum 100 files allowed at once");
      return;
    }

    // Validate file types
    const invalidFiles = files.filter(
      (file) => file.type !== "application/pdf"
    );
    if (invalidFiles.length > 0) {
      alert("Only PDF files are allowed");
      return;
    }

    setSelectedFiles(files);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setCurrentBatch(0);

    // Calculate batches
    const batches = Math.ceil(selectedFiles.length / batchSize);
    setTotalBatches(batches);

    // Reset batch results
    setBatchResults({
      totalUploaded: 0,
      totalErrors: 0,
      errors: [],
    });

    // Reset failed uploads
    setFailedUploads([]);

    // Track results locally during upload
    let localResults = {
      totalUploaded: 0,
      totalErrors: 0,
      errors: [],
    };
    let localFailedUploads = [];

    try {
      for (let i = 0; i < batches; i++) {
        setCurrentBatch(i + 1);

        // Get files for current batch
        const startIndex = i * batchSize;
        const endIndex = Math.min(startIndex + batchSize, selectedFiles.length);
        const batchFiles = selectedFiles.slice(startIndex, endIndex);

        // Create FormData for current batch
        const formData = new FormData();
        batchFiles.forEach((file) => {
          formData.append("files[]", file);
        });

        try {
          const response = await BaseApi().post(
            "/form16-documents/bulk-upload",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          const result = response.data;

          // Update local results
          localResults.totalUploaded += result.total_uploaded;
          localResults.totalErrors += result.total_errors;
          localResults.errors = [...localResults.errors, ...result.errors];

          // Add individual failed files to local failed uploads
          if (result.errors && result.errors.length > 0) {
            result.errors.forEach((error, errorIndex) => {
              // Try to extract filename from error message with multiple patterns
              let fileName = null;

              // Pattern 1: "File: filename.pdf - error message"
              let fileNameMatch = error.match(/File: (.+\.pdf) -/);
              if (fileNameMatch) {
                fileName = fileNameMatch[1];
              }

              // Pattern 2: "filename.pdf" at the end
              if (!fileName) {
                fileNameMatch = error.match(
                  /([A-Z0-9]+_[A-Z]+_\d{4}-\d{2}\.pdf)/
                );
                if (fileNameMatch) {
                  fileName = fileNameMatch[1];
                }
              }

              // Pattern 3: "in file: filename.pdf"
              if (!fileName) {
                fileNameMatch = error.match(/in file: (.+\.pdf)/i);
                if (fileNameMatch) {
                  fileName = fileNameMatch[1];
                }
              }

              // If still no filename found, try to find it in the batch files
              if (!fileName && errorIndex < batchFiles.length) {
                fileName = batchFiles[errorIndex].name;
              }

              // Final fallback
              if (!fileName) {
                fileName = `unknown_file_${i + 1}_${errorIndex + 1}.pdf`;
              }

              // Find the actual file size from the batch files
              const actualFile = batchFiles.find(
                (file) => file.name === fileName
              );
              const fileSize = actualFile ? actualFile.size : 0;

              localFailedUploads.push({
                fileName: fileName,
                fileSize: fileSize,
                error: error,
                batchNumber: i + 1,
              });
            });
          }

          // Update batch results state for UI
          setBatchResults({
            totalUploaded: localResults.totalUploaded,
            totalErrors: localResults.totalErrors,
            errors: localResults.errors,
          });

          // Update failed uploads state for UI
          setFailedUploads([...localFailedUploads]);

          // Update progress
          const progress = ((i + 1) / batches) * 100;
          setUploadProgress(progress);
        } catch (error) {
          const message =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            error.toString();

          // Add each failed file to local failed uploads
          batchFiles.forEach((file) => {
            localFailedUploads.push({
              fileName: file.name,
              fileSize: file.size,
              error: message,
              batchNumber: i + 1,
            });
          });

          // Add batch error to local results
          localResults.totalErrors += batchFiles.length;
          localResults.errors = [
            ...localResults.errors,
            `Batch ${i + 1} failed: ${message}`,
          ];

          // Update batch results state for UI
          setBatchResults({
            totalUploaded: localResults.totalUploaded,
            totalErrors: localResults.totalErrors,
            errors: localResults.errors,
          });

          // Update failed uploads state for UI
          setFailedUploads([...localFailedUploads]);
        }
      }

      // Set final results using local results
      setUploadResults({
        message: `Batch upload completed. ${localResults.totalUploaded} files uploaded successfully.${localFailedUploads.length > 0 ? ` ${localFailedUploads.length} files failed to upload.` : ""}`,
        total_uploaded: localResults.totalUploaded,
        total_errors: localResults.totalErrors,
        errors: localResults.errors,
      });

      // Set final failed uploads
      setFailedUploads(localFailedUploads);

      // Clear selected files and reload documents
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      loadDocuments();
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      alert("Upload failed: " + message);
    } finally {
      setUploading(false);
      setUploadProgress(100);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSearch = async (url = null) => {
    setLoading(true);
    try {
      if (url && typeof url === "string") {
        const response = await BaseApi().get(url);
        setDocuments(response.data);
      } else {
        const params = new URLSearchParams();
        if (searchParams.pan) params.append("pan", searchParams.pan);
        if (searchParams.part) params.append("part", searchParams.part);
        if (searchParams.session)
          params.append("session", searchParams.session);

        const response = await BaseApi().get(
          `/form16-documents/search?${params}`
        );
        setDocuments(response.data);
      }
    } catch (error) {
      console.error("Error searching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await BaseApi().delete(`/form16-documents/${id}`);
      alert("Document deleted successfully");
      loadDocuments();
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      alert("Error deleting document: " + message);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await BaseApi().get(
        `form16-documents/${doc.id}/download`,
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Form16_${doc.pan}_${doc.part}_${doc.session}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download document");
      console.error("Download error:", err);
    }
  };

  const downloadFailedFilesCSV = () => {
    if (failedUploads.length === 0) return;

    // Create CSV content
    const headers = [
      "Serial",
      "File Name",
      "File Size (KB)",
      "Batch Number",
      "Error Message",
    ];
    const csvContent = [
      headers.join(","),
      ...failedUploads.map((file, index) =>
        [
          index + 1,
          `"${file.fileName}"`,
          file.fileSize > 0 ? (file.fileSize / 1024).toFixed(1) : "N/A",
          file.batchNumber,
          `"${file.error.replace(/"/g, '""')}"`, // Escape quotes in error message
        ].join(",")
      ),
    ].join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `failed_uploads_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className='m-10'>
      <div className='max-w-6xl mx-auto'>
        <h2 className='text-2xl font-bold mb-6 text-center'>
          Form 16 Documents Management
        </h2>

        {/* File Selection */}
        <div className='mb-6 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center'>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            accept='.pdf'
            onChange={handleFileSelect}
            className='hidden'
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className='px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600'
          >
            Select PDF Files (Max 100)
          </button>
          <p className='mt-2 text-sm text-gray-600'>
            Files should be named as: PAN_PART_SESSION.pdf (e.g.,
            CCTPM5542G_PARTA_2023-24.pdf)
          </p>
          <p className='mt-1 text-xs text-blue-600'>
            Large batches will be uploaded in groups of {batchSize} files to
            ensure reliability.
          </p>
        </div>

        {/* Selected Files */}
        {selectedFiles.length > 0 && (
          <div className='mb-6'>
            <h3 className='text-lg font-semibold mb-3'>
              Selected Files ({selectedFiles.length})
            </h3>
            <div className='max-h-60 overflow-y-auto border rounded-lg p-4'>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className='flex items-center justify-between py-2 border-b last:border-b-0'
                >
                  <span className='text-sm'>{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className='text-red-500 hover:text-red-700 text-sm'
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        {selectedFiles.length > 0 && (
          <div className='text-center mb-6'>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className={`px-8 py-3 rounded-lg font-semibold ${
                uploading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-500 hover:bg-green-600 text-white"
              }`}
            >
              {uploading
                ? "Uploading..."
                : `Upload ${selectedFiles.length} Files`}
            </button>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className='mb-6'>
            <div className='w-full bg-gray-200 rounded-full h-2.5'>
              <div
                className='bg-blue-600 h-2.5 rounded-full transition-all duration-300'
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className='text-center mt-2 text-sm text-gray-600'>
              Uploading batch {currentBatch} of {totalBatches}...{" "}
              {Math.round(uploadProgress)}%
            </p>
            <p className='text-center text-xs text-gray-500'>
              Uploaded: {batchResults.totalUploaded} files | Errors:{" "}
              {batchResults.totalErrors}
            </p>
          </div>
        )}

        {/* Upload Results */}
        {uploadResults && (
          <div className='mt-6 p-4 border rounded-lg'>
            <h3 className='text-lg font-semibold mb-3'>Upload Results</h3>
            <div className='mb-3 p-3 bg-blue-50 rounded-lg'>
              <p className='text-sm text-blue-800'>{uploadResults.message}</p>
            </div>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <span className='font-medium'>Total Uploaded:</span>{" "}
                {uploadResults.total_uploaded}
              </div>
              <div>
                <span className='font-medium'>Total Errors:</span>{" "}
                {uploadResults.total_errors}
              </div>
            </div>

            {uploadResults.errors.length > 0 && (
              <div className='mt-4'>
                <h4 className='font-medium text-red-600 mb-2'>Errors:</h4>
                <div className='max-h-40 overflow-y-auto text-red-600 text-sm'>
                  {uploadResults.errors.map((error, index) => (
                    <div key={index} className='py-1'>
                      • {error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Failed Uploads Table */}
        {failedUploads.length > 0 && (
          <div className='mt-6 p-4 border rounded-lg border-red-200 bg-red-50'>
            <div className='flex justify-between items-center mb-3'>
              <h3 className='text-lg font-semibold text-red-800'>
                Failed Uploads ({failedUploads.length})
              </h3>
              <button
                onClick={downloadFailedFilesCSV}
                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium'
              >
                Download CSV
              </button>
            </div>
            <div className='overflow-x-auto'>
              <table className='min-w-full bg-white border rounded-lg'>
                <thead className='bg-red-100'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>
                      #
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>
                      File Name
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>
                      File Size
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>
                      Batch
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-red-700 uppercase tracking-wider'>
                      Error
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {failedUploads.map((failedFile, index) => (
                    <tr key={index} className='bg-red-50'>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {index + 1}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {failedFile.fileName}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {(failedFile.fileSize / 1024).toFixed(1)} KB
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {failedFile.batchNumber}
                      </td>
                      <td className='px-6 py-4 text-sm text-red-600 max-w-xs  '>
                        {failedFile.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='mt-3 text-sm text-red-700'>
              <p>
                These files failed to upload. You can download the file names to
                retry uploading them individually.
              </p>
            </div>
          </div>
        )}

        {/* Search Section */}
        <div className='mb-6 p-4 border rounded-lg'>
          <h3 className='text-lg font-semibold mb-3'>Search Documents</h3>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <input
              type='text'
              placeholder='Search by PAN'
              value={searchParams.pan}
              onChange={(e) =>
                setSearchParams((prev) => ({ ...prev, pan: e.target.value }))
              }
              className='px-3 py-2 border rounded-lg'
            />
            <select
              value={searchParams.part}
              onChange={(e) =>
                setSearchParams((prev) => ({ ...prev, part: e.target.value }))
              }
              className='px-3 py-2 border rounded-lg'
            >
              <option value=''>All Parts</option>
              <option value='PARTA'>Form 16A</option>
              <option value='PARTB'>Form 16B</option>
            </select>
            <select
              value={searchParams.session}
              onChange={(e) =>
                setSearchParams((prev) => ({
                  ...prev,
                  session: e.target.value,
                }))
              }
              className='px-3 py-2 border rounded-lg'
            >
              <option value=''>All Assessment Years</option>
              {academicSessions
                .filter((session) => session.id >= 5)
                .map((session) => (
                  <option
                    key={session.id}
                    value={formatAcademicLabel(session.label)}
                  >
                    {formatAcademicLabel(session.label)}
                  </option>
                ))}
            </select>
            <button
              onClick={handleSearch}
              className='px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600'
            >
              Search
            </button>
          </div>
        </div>

        {/* Documents List */}
        <div className='mb-6'>
          <div className='flex justify-between items-center mb-3'>
            <h3 className='text-lg font-semibold'>
              Documents ({documents.total})
            </h3>
            <button
              onClick={() => loadDocuments()}
              className='px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600'
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className='text-center py-8'>Loading...</div>
          ) : documents.data?.length === 0 ? (
            <div className='text-center py-8 text-gray-500'>
              No documents found
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full bg-white border rounded-lg'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      #
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      PAN
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Part
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Assessment Year
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Uploaded
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-200'>
                  {documents.data?.map((doc, index) => (
                    <tr key={doc.id}>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {(documents.current_page - 1) * documents.per_page +
                          index +
                          1}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {doc.pan}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {doc.part === "PARTA" ? "Form 16A" : "Form 16B"}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {doc.session}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                        {new Date(doc.created_at).toLocaleDateString()}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                        <button
                          onClick={() => handleDownload(doc)}
                          className='text-blue-600 hover:text-blue-900 mr-3'
                        >
                          Download
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className='text-red-600 hover:text-red-900'
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {documents.total > documents.per_page && (
            <div className='my-5 pb-10'>
              <Pagination
                links={documents?.links}
                onClick={(url) => {
                  // Ensure url is a string
                  const urlString = typeof url === "string" ? url : null;

                  // Check if we're in search mode or normal mode
                  const hasSearchParams =
                    searchParams.pan ||
                    searchParams.part ||
                    searchParams.session;
                  if (hasSearchParams) {
                    handleSearch(urlString);
                  } else {
                    loadDocuments(urlString);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
